import { useNavigate } from "react-router-dom";

import { Alert, Button, ConfirmDialog } from "@a2z/ui";
import { getSellerPath } from "@/lib/routing/portal-urls";

import { useState } from "react";

import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { useDeleteProductMutation } from "@/modules/products/services/useDeleteProductMutation";

export function ProductDeleteButton({
  locale,
  storeSlug,
  productId,
}: {
  locale: string;
  storeSlug: string;
  productId: string;
}) {
  const navigate = useNavigate();
  const t = useTranslations();
  const [error, setError] = useState<string | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { mutateAsync: deleteProduct, isPending: isSubmitting } =
    useDeleteProductMutation();

  const handleDelete = async () => {
    setError(null);
    try {
      await deleteProduct({ storeSlug, productId });
    } catch (caughtError) {
      setError(
        caughtError instanceof Error ? caughtError.message : t(keys.errors.generic),
      );
      return;
    }

    navigate(
      `${getSellerPath(locale, storeSlug)}/products`
    , { replace: true });
  };

  return (
    <div className="flex flex-col gap-3 max-lg:w-full">
      {error ? <Alert message={error} helperText={t(keys.errors.recovery)} /> : null}
      {/* Mobile: the design's full-width red ghost. Desktop keeps the shared
          Button so nothing changes at lg+. */}
      <button
        type="button"
        onClick={() => setIsDialogOpen(true)}
        disabled={isSubmitting}
        data-testid="product-delete-trigger"
        className="hidden w-full items-center justify-center gap-2 rounded-[13px] border border-[color:color-mix(in_oklab,var(--error-500)_40%,transparent)] bg-transparent px-4 py-[13px] text-[14px] font-bold text-[color:var(--error-500)] transition-colors hover:bg-[color:color-mix(in_oklab,var(--error-500)_9%,transparent)] disabled:opacity-50 max-lg:flex"
      >
        <TrashIcon />
        {t(keys.common.delete)}
      </button>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setIsDialogOpen(true)}
        disabled={isSubmitting}
        data-testid="product-delete-trigger-desktop"
        className="self-start max-lg:hidden"
        style={{
          color: "var(--danger-500, var(--error-500))",
          borderColor: "color-mix(in oklab, var(--danger-500, var(--error-500)) 40%, transparent)",
        }}
        leadingIcon={<TrashIcon />}
      >
        {t(keys.common.delete)}
      </Button>
      <ConfirmDialog
        isOpen={isDialogOpen}
        title={t(keys.products.deleteConfirmTitle)}
        body={t(keys.products.deleteConfirmBody)}
        confirmLabel={t(keys.common.delete)}
        cancelLabel={t(keys.common.cancel)}
        isSubmitting={isSubmitting}
        onConfirm={() => {
          setIsDialogOpen(false);
          void handleDelete();
        }}
        onCancel={() => setIsDialogOpen(false)}
      />
    </div>
  );
}

function TrashIcon() {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m2 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
      <path d="M10 11v6M14 11v6" />
    </svg>
  );
}
